import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertTriangle, Shield, ShieldAlert, ShieldCheck, Activity, Sparkles } from "lucide-react";
import { CyberResilienceDial } from "@/components/reviewer/CyberResilienceDial";
import { useAutosaveIndicator } from "@/hooks/useAutosaveIndicator";
import { SaveChip } from "@/components/ui/SaveChip";
import { NistFunctionScorecard } from "@/components/reviewer/NistFunctionScorecard";
import { NistFunctionRatingsAndCommentary } from "@/components/reviewer/NistFunctionRatingsAndCommentary";
import { CyberResilienceFunctionalAdequacy } from "@/components/reviewer/CyberResilienceFunctionalAdequacy";
import { CsvDomainScorecard } from "@/components/reviewer/CsvDomainScorecard";
import { CsvDomainRatingsAndCommentary } from "@/components/reviewer/CsvDomainRatingsAndCommentary";
import { TrafficLightBars, type FnScore } from "@/components/report/TrafficLightBars";
import type { FnKey } from "@/reports/narratives";
import { buildTelecomExecSummaryDraft, buildTelecomKeyStrengths, buildTelecomKeyAreas } from "@/reports/telecom/executiveScaffold";
import { buildTelecomContextBlock, type TelecomContextBlock } from "@/reports/telecom/operatingContext";
import { TelecomVendorExposurePanel } from "@/components/reviewer/TelecomVendorExposurePanel";
import { useToast } from "@/hooks/use-toast";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";

const sb: any = supabase as any;

type Props = {
  assessmentId: string;
  disabled?: boolean;
  nistRefreshKey?: number;
  csvDomainRefreshKey?: number;
  questionnaireKey?: string;
  orgName?: string | null;
};

type Metrics = {
  totalFindings: number;
  highFindings: number;
  moderateFindings: number;
  lowFindings: number;
  totalControls: number;
  implemented: number;
  partial: number;
  notImplemented: number;
};

type FormData = {
  executive_summary: string;
  key_strengths: string;
  key_areas_for_improvement: string;
  cyber_resilience_score: number;
  cyber_resilience_override: boolean;
  override_level: string;
};

const MATURITY_LEVELS = ["Minimal", "Adequate", "Strong", "Extensive"] as const;
type MaturityLevel = typeof MATURITY_LEVELS[number];

function averageToMaturity(avg: number): MaturityLevel {
  // Aligned with calculate_csv_domain_scores / pm_recompute_assessment_scores
  // stricter consulting model: <0.50 Minimal | <1.50 Adequate | <1.85 Strong | >=1.85 Extensive
  if (avg < 0.50) return "Minimal";
  if (avg < 1.50) return "Adequate";
  if (avg < 1.85) return "Strong";
  return "Extensive";
}

function maturityToNumeric(level: MaturityLevel): number {
  switch (level) {
    case "Minimal": return 0.25;
    case "Adequate": return 0.75;
    case "Strong": return 1.25;
    case "Extensive": return 1.75;
  }
}

const EMPTY_FORM: FormData = {
  executive_summary: "",
  key_strengths: "",
  key_areas_for_improvement: "",
  cyber_resilience_score: 0,
  cyber_resilience_override: false,
  override_level: "Minimal",
};

function composeGeneratedContextText(block: TelecomContextBlock | null): string {
  if (!block) return "";
  const parts: string[] = [];
  if (block.generatedSummary) parts.push(block.generatedSummary);
  if (block.generatedBullets?.length) {
    parts.push(block.generatedBullets.map((b) => `• ${b}`).join("\n"));
  }
  return parts.join("\n\n");
}

export function ExecutiveSummaryForm({ assessmentId, disabled, nistRefreshKey, csvDomainRefreshKey, questionnaireKey, orgName }: Props) {
  const { toast } = useToast();
  const [draftLoading, setDraftLoading] = useState(false);
  const isTelecomV2 = questionnaireKey === "telecom_v2";

  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [calculatedAvg, setCalculatedAvg] = useState<number>(0);
  const [controlsEvaluated, setControlsEvaluated] = useState<number>(0);
  const [nistFnScores, setNistFnScores] = useState<FnScore[]>([]);
  const [contextBlock, setContextBlock] = useState<TelecomContextBlock | null>(null);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null);
  const [confirmedBy, setConfirmedBy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<boolean>(false);
  // Phase 2.13.1 — reviewer-editable override
  const [overrideEnabled, setOverrideEnabled] = useState<boolean>(false);
  const [overrideText, setOverrideText] = useState<string>("");
  const [vendorExposureConfirmed, setVendorExposureConfirmed] = useState<boolean>(false);
  const [editingContext, setEditingContext] = useState<boolean>(false);
  const [editDraft, setEditDraft] = useState<string>("");
  const [savingOverride, setSavingOverride] = useState<boolean>(false);
  const [findings, setFindings] = useState<any[]>([]);
  const [ratings, setRatings] = useState<any[]>([]);
  const [tvFindings, setTvFindings] = useState<any[]>([]);
  const autosave = useAutosaveIndicator(1000, 2500);
  const debounceRef = useRef<number | null>(null);
  const handleOverallScoreChange = useCallback((score: number) => {
    setCalculatedAvg(score);
  }, []);
  const loadedRef = useRef(false);

  const calculatedLevel = useMemo(() => averageToMaturity(calculatedAvg), [calculatedAvg]);

  const effectiveLevel: MaturityLevel = form.cyber_resilience_override
    ? (form.override_level as MaturityLevel)
    : calculatedLevel;

  const doSave = useCallback(async (data: FormData) => {
    autosave.begin();
    try {
      const scoreToSave = data.cyber_resilience_override
        ? maturityToNumeric(data.override_level as MaturityLevel)
        : calculatedAvg;

      const { error } = await sb.rpc("reviewer_upsert_executive_summary", {
        p_assessment_id: assessmentId,
        p_executive_summary: data.executive_summary || null,
        p_key_strengths: data.key_strengths || null,
        p_key_areas_for_improvement: data.key_areas_for_improvement || null,
        p_cyber_resilience_score: scoreToSave,
        p_cyber_resilience_override: data.cyber_resilience_override,
      });
      if (error) throw error;
      autosave.succeed();
    } catch {
      autosave.fail();
    }
  }, [assessmentId, calculatedAvg, autosave]);

  const scheduleAutosave = useCallback((nextForm: FormData) => {
    if (!loadedRef.current) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => doSave(nextForm), 1000);
  }, [doSave]);

  useEffect(() => () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
  }, []);

  const update = useCallback((field: keyof FormData, value: string | number | boolean) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      scheduleAutosave(next);
      return next;
    });
  }, [scheduleAutosave]);

  const insertSuggestedDraft = useCallback(async () => {
    const hasExistingContent =
      (form.executive_summary?.trim().length ?? 0) > 0 ||
      (form.key_strengths?.trim().length ?? 0) > 0 ||
      (form.key_areas_for_improvement?.trim().length ?? 0) > 0;
    if (hasExistingContent) {
      const ok = window.confirm(
        "This will overwrite the Executive Summary, Key Strengths, and Key Areas for Improvement with a freshly-generated maturity-based draft (no decimal scores, no NIST function names).\n\nContinue?"
      );
      if (!ok) return;
    }
    setDraftLoading(true);
    try {
      const [nistRes, csvRes, findingsRes, esRes] = await Promise.all([
        sb.rpc("calculate_nist_function_scores", { p_assessment_id: assessmentId }),
        sb.rpc("calculate_csv_domain_scores", { p_assessment_id: assessmentId }),
        sb.rpc("reviewer_get_findings", { p_assessment_id: assessmentId }),
        sb.rpc("reviewer_get_executive_summary", { p_assessment_id: assessmentId }),
      ]);
      const nistScores = (nistRes.data || []).map((s: any) => ({
        name: s.function_name,
        score: Number(s.average_score),
        maturity: s.maturity_level,
      }));
      const csvScores = (csvRes.data || []).map((s: any) => ({
        name: s.domain_name ?? s.csv_domain ?? s.name ?? "Domain",
        score: Number(s.score ?? s.average_score ?? 0),
      }));
      const findings = (findingsRes.data || []) as any[];
      const highCriticalCount = findings.filter(f => f?.risk_level === "High" || f?.risk_level === "Critical").length;
      const esRow = Array.isArray(esRes.data) ? esRes.data[0] : esRes.data;
      const score = esRow?.cyber_resilience_score != null ? Number(esRow.cyber_resilience_score) : (calculatedAvg || null);

      const draft = buildTelecomExecSummaryDraft({
        orgName: orgName ?? "The organisation",
        cyberResilienceScore: score,
        nistScores,
        csvScores,
        highCriticalCount,
        totalFindings: findings.length,
      });
      const strengths = buildTelecomKeyStrengths(csvScores);
      const areas = buildTelecomKeyAreas(csvScores);
      // Apply all three together so reviewer can refine in one pass.
      setForm(prev => {
        const next = { ...prev, executive_summary: draft, key_strengths: strengths, key_areas_for_improvement: areas };
        scheduleAutosave(next);
        return next;
      });
      toast({ title: "Suggested draft inserted", description: "Executive summary, strengths, and improvement areas populated. Edit freely — changes autosave." });
    } catch (e: any) {
      toast({ title: "Could not generate draft", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setDraftLoading(false);
    }
  }, [assessmentId, orgName, calculatedAvg, scheduleAutosave, toast, form.executive_summary, form.key_strengths, form.key_areas_for_improvement]);


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [esResult, progressResult, ratingsResult, nistResult, profileResult, findingsResult, scopeResult, tvResult] = await Promise.all([
        sb.rpc("reviewer_get_executive_summary", { p_assessment_id: assessmentId }),
        sb.rpc("reviewer_get_progress_summary"),
        sb.rpc("reviewer_get_ratings", { p_assessment_id: assessmentId }),
        sb.rpc("calculate_nist_function_scores", { p_assessment_id: assessmentId }),
        isTelecomV2
          ? sb.rpc("reviewer_get_assessment_profile", { p_assessment_id: assessmentId })
          : Promise.resolve({ data: null, error: null }),
        isTelecomV2
          ? sb.rpc("reviewer_get_findings", { p_assessment_id: assessmentId })
          : Promise.resolve({ data: [], error: null }),
        isTelecomV2
          ? sb.rpc("reviewer_get_infrastructure_scope", { p_assessment_id: assessmentId }).then(
              (r: any) => r,
              (error: any) => ({ data: null, error }),
            )
          : Promise.resolve({ data: null, error: null }),
        isTelecomV2
          ? sb.rpc("reviewer_list_technical_validation_findings", { p_assessment_id: assessmentId }).then(
              (r: any) => r,
              (error: any) => ({ data: [], error }),
            )
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (isTelecomV2 && !tvResult?.error) {
        setTvFindings(Array.isArray(tvResult.data) ? tvResult.data : []);
      }

      if (!isTelecomV2 || !findingsResult.error) {
        setFindings(Array.isArray(findingsResult.data) ? findingsResult.data : []);
      }


      if (isTelecomV2) {
        const profileRow = Array.isArray(profileResult?.data) ? profileResult.data[0] : profileResult?.data;
        const scopeRow = Array.isArray(scopeResult?.data) ? scopeResult.data[0] : scopeResult?.data;
        setConfirmed(!!profileRow?.operating_context_confirmed);
        setConfirmedAt(profileRow?.operating_context_confirmed_at ?? null);
        setConfirmedBy(profileRow?.operating_context_confirmed_by ?? null);
        setVendorExposureConfirmed(!!profileRow?.vendor_exposure_confirmed);
        const profOverride = profileRow?.operating_context_override ?? "";
        const profOverrideEnabled = !!profileRow?.operating_context_override_enabled;
        setOverrideText(profOverride);
        setOverrideEnabled(profOverrideEnabled);
        setContextBlock(
          buildTelecomContextBlock({
            orgName: orgName || profileRow?.client_organization || null,
            parentOrganization: profileRow?.parent_organization || null,
            headquartersLocation: profileRow?.headquarters_location || null,
            region: profileRow?.region || null,
            sector: profileRow?.sector || null,
            frameworks: Array.isArray(profileRow?.methodology_frameworks) ? profileRow.methodology_frameworks : [],
            notes: profileRow?.operating_context_notes || null,
            legacyMethodology: profileRow?.methodology || null,
            serviceModels: Array.isArray(scopeRow?.telecom_service_model) ? scopeRow.telecom_service_model : [],
            componentsPresent: Array.isArray(scopeRow?.components_present) ? scopeRow.components_present : [],
            componentsAssessed: Array.isArray(scopeRow?.components_assessed) ? scopeRow.components_assessed : [],
            confirmed: !!profileRow?.operating_context_confirmed,
            override: profOverride,
            overrideEnabled: profOverrideEnabled,
          })
        );
      } else {
        setContextBlock(null);
      }

      if (!esResult.error) {
        const row = Array.isArray(esResult.data) ? esResult.data[0] : esResult.data;
        if (row) {
          setForm({
            executive_summary: row.executive_summary ?? "",
            key_strengths: row.key_strengths ?? "",
            key_areas_for_improvement: row.key_areas_for_improvement ?? "",
            cyber_resilience_score: Number(row.cyber_resilience_score ?? 0),
            cyber_resilience_override: row.cyber_resilience_override ?? false,
            override_level: row.cyber_resilience_override
              ? averageToMaturity(Number(row.cyber_resilience_score ?? 0))
              : "Minimal",
          });
        }
      }

      if (!ratingsResult.error && ratingsResult.data) {
        const ratings = Array.isArray(ratingsResult.data) ? ratingsResult.data : [];
        setRatings(ratings);
        const scored = ratings.filter((r: any) => r.reviewer_score != null);
        setControlsEvaluated(scored.length);
        if (scored.length > 0) {
          const mapped = scored.map((r: any) => {
            const s = Number(r.reviewer_score);
            if (s === 0) return 0;
            if (s === 100) return 2;
            return 1;
          });
          const avg = mapped.reduce((a: number, b: number) => a + b, 0) / mapped.length;
          setCalculatedAvg(avg);
        }
      }

      if (!progressResult.error && progressResult.data) {
        const rows = Array.isArray(progressResult.data) ? progressResult.data : [progressResult.data];
        const match = rows.find((r: any) => r.assessment_id === assessmentId);
        if (match) {
          setMetrics({
            totalFindings: Number(match.findings_count ?? 0),
            highFindings: Number(match.high_risk_findings ?? 0),
            moderateFindings: Number(match.medium_risk_findings ?? 0),
            lowFindings: Number(match.findings_count ?? 0) - Number(match.high_risk_findings ?? 0) - Number(match.medium_risk_findings ?? 0),
            totalControls: Number(match.total_questions ?? 0),
            implemented: Number(match.implemented_count ?? 0),
            partial: Number(match.partial_count ?? 0),
            notImplemented: Number(match.not_implemented_count ?? 0),
          });
        }
      }

      if (!nistResult.error && Array.isArray(nistResult.data)) {
        const allowed: FnKey[] = ["Govern", "Identify", "Protect", "Detect", "Respond", "Recover"];
        const fnScores: FnScore[] = (nistResult.data as any[])
          .filter((s) => allowed.includes(s.function_name as FnKey))
          .map((s) => ({
            fn: s.function_name as FnKey,
            // average_score is 0–2; convert to 0–100
            value: Math.round((Number(s.average_score) / 2) * 100),
          }));
        setNistFnScores(fnScores);
      }
    } catch (e) {
      console.error("[ExecutiveSummaryForm] load error:", e);
    } finally {
      setLoading(false);
      loadedRef.current = true;
    }
  }, [assessmentId, isTelecomV2, orgName]);

  useEffect(() => { loadData(); }, [loadData, nistRefreshKey]);

  // Findings Heatmap — distribution of OFFICIAL findings by their reviewer-assigned
  // risk_level only (High / Moderate / Low). No reviewer_score, no implementation
  // status, no likelihood/impact methodology. One dot = one finding.
  const findingsByRisk = useMemo(() => {
    const buckets: Record<"High" | "Moderate" | "Low", number> = { High: 0, Moderate: 0, Low: 0 };
    for (const f of findings) {
      const raw = String(f?.risk_level ?? "").trim().toLowerCase();
      if (raw === "high" || raw === "critical") buckets.High += 1;
      else if (raw === "moderate" || raw === "medium") buckets.Moderate += 1;
      else if (raw === "low") buckets.Low += 1;
    }
    return buckets;
  }, [findings]);

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading executive summary…</div>;
  }

  // --- Derived (non-hook) values ---
  // Risk Distribution — counts by severity (already-computed metrics)
  const riskDistributionData = metrics
    ? [
        { label: "High", value: metrics.highFindings, cls: "bg-destructive" },
        { label: "Moderate", value: metrics.moderateFindings, cls: "bg-amber-500" },
        { label: "Low", value: metrics.lowFindings, cls: "bg-blue-500" },
      ]
    : [];
  const riskDistTotal = riskDistributionData.reduce((s, r) => s + (r.value || 0), 0);

  // --- Existing JSX blocks captured as variables to preserve all behavior verbatim ---

  const operatingContextNode = isTelecomV2 && contextBlock ? (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <Activity className="h-4 w-4 text-primary" />
          Operating &amp; Regulatory Context
          <span
            className={
              "text-[11px] font-medium px-2 py-0.5 rounded-full ml-1 " +
              (confirmed
                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300")
            }
          >
            {confirmed ? "Confirmed" : "Draft / Unconfirmed"}
          </span>
          {confirmed && confirmedAt && (
            <span className="text-[11px] font-normal text-muted-foreground">
              on {new Date(confirmedAt).toLocaleDateString()}
            </span>
          )}
          <span
            className={
              "text-[11px] font-medium px-2 py-0.5 rounded-full " +
              (overrideEnabled
                ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300"
                : "bg-muted text-muted-foreground")
            }
          >
            {overrideEnabled ? "Reviewer Customized" : "System Generated"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!confirmed && (
          <div className="rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs text-amber-900 dark:text-amber-200">
            Operating context has not yet been confirmed by the reviewer.
            The summary below is based on available assessment profile and scope inputs and
            should be reviewed before report finalisation.
          </div>
        )}

        {editingContext ? (
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Edit Operating &amp; Regulatory Context
            </label>
            <Textarea
              rows={12}
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              placeholder="Write the operating and regulatory context narrative…"
              disabled={disabled || savingOverride}
            />
            <p className="text-[11px] text-muted-foreground italic">
              Saving enables the customized version in the report. Clearing the override returns the report to the system-generated version. Frameworks, notes, and methodology cards continue to render from assessment-profile inputs.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={disabled || savingOverride || !editDraft.trim()}
                onClick={async () => {
                  setSavingOverride(true);
                  try {
                    const { data, error } = await sb.rpc("reviewer_update_operating_context_override", {
                      p_assessment_id: assessmentId,
                      p_text: editDraft,
                      p_enabled: true,
                    });
                    if (error) throw error;
                    const row = Array.isArray(data) ? data[0] : data;
                    const nextText = row?.operating_context_override ?? editDraft;
                    const nextEnabled = !!row?.operating_context_override_enabled;
                    setOverrideText(nextText);
                    setOverrideEnabled(nextEnabled);
                    setContextBlock((prev) =>
                      prev
                        ? {
                            ...prev,
                            override: nextText,
                            overrideEnabled: nextEnabled,
                            summary: nextEnabled ? nextText.trim() : prev.generatedSummary,
                            bullets: nextEnabled ? [] : prev.generatedBullets,
                          }
                        : prev,
                    );
                    setEditingContext(false);
                    toast({ title: "Customized context saved", description: "The report will now render the reviewer-customized version." });
                  } catch (e: any) {
                    toast({ title: "Could not save context", description: e?.message ?? "Unknown error", variant: "destructive" });
                  } finally {
                    setSavingOverride(false);
                  }
                }}
              >
                {savingOverride ? "Saving…" : "Save customized context"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={savingOverride}
                onClick={() => setEditingContext(false)}
              >
                Cancel
              </Button>
              {overrideEnabled && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={disabled || savingOverride}
                  onClick={async () => {
                    setSavingOverride(true);
                    try {
                      const { data, error } = await sb.rpc("reviewer_update_operating_context_override", {
                        p_assessment_id: assessmentId,
                        p_text: overrideText,
                        p_enabled: false,
                      });
                      if (error) throw error;
                      const row = Array.isArray(data) ? data[0] : data;
                      const nextEnabled = !!row?.operating_context_override_enabled;
                      setOverrideEnabled(nextEnabled);
                      setContextBlock((prev) =>
                        prev
                          ? {
                              ...prev,
                              overrideEnabled: nextEnabled,
                              summary: prev.generatedSummary,
                              bullets: prev.generatedBullets,
                            }
                          : prev,
                      );
                      setEditingContext(false);
                      toast({ title: "Reverted to system-generated", description: "The report will render the system-generated version. Your customized text is preserved." });
                    } catch (e: any) {
                      toast({ title: "Could not revert", description: e?.message ?? "Unknown error", variant: "destructive" });
                    } finally {
                      setSavingOverride(false);
                    }
                  }}
                >
                  Use system-generated instead
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-foreground whitespace-pre-wrap">{contextBlock.summary}</p>
            {contextBlock.bullets.length > 0 && (
              <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
                {contextBlock.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
          </>
        )}

        {contextBlock.frameworkBlocks.length > 0 && (
          <div className="space-y-3 pt-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Frameworks selected for assessment alignment
            </div>
            {contextBlock.frameworkBlocks.map((fb) => (
              <div key={fb.framework} className="rounded-md border bg-background/60 p-3 space-y-1.5">
                <div className="text-sm font-semibold text-foreground">{fb.framework}</div>
                <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Applicability:</span> {fb.applicability}</p>
                <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Requirements:</span> {fb.requirements}</p>
                <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Purpose:</span> {fb.purpose}</p>
              </div>
            ))}
          </div>
        )}

        {contextBlock.notes && (
          <div className="pt-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              Additional Context
            </div>
            <p className="text-sm text-foreground whitespace-pre-wrap">{contextBlock.notes}</p>
          </div>
        )}

        {contextBlock.legacyMethodology && (
          <div className="pt-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              Methodology Notes
            </div>
            <p className="text-sm text-foreground whitespace-pre-wrap">{contextBlock.legacyMethodology}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
          <Button
            type="button"
            size="sm"
            variant={confirmed ? "outline" : "default"}
            disabled={disabled || confirming}
            onClick={async () => {
              setConfirming(true);
              try {
                const next = !confirmed;
                const { data, error } = await sb.rpc("reviewer_set_operating_context_confirmation", {
                  p_assessment_id: assessmentId,
                  p_confirmed: next,
                });
                if (error) throw error;
                const row = Array.isArray(data) ? data[0] : data;
                setConfirmed(!!row?.operating_context_confirmed);
                setConfirmedAt(row?.operating_context_confirmed_at ?? null);
                setConfirmedBy(row?.operating_context_confirmed_by ?? null);
                setContextBlock((prev) => prev ? { ...prev, confirmed: !!row?.operating_context_confirmed } : prev);
                toast({
                  title: next ? "Operating context confirmed" : "Confirmation cleared",
                  description: next
                    ? "The report will render this context as reviewer-confirmed."
                    : "The report will mark this context as draft / unconfirmed.",
                });
              } catch (e: any) {
                toast({ title: "Could not update confirmation", description: e?.message ?? "Unknown error", variant: "destructive" });
              } finally {
                setConfirming(false);
              }
            }}
          >
            {confirmed ? "Clear confirmation" : "Confirm Operating & Regulatory Context"}
          </Button>
          {!editingContext && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => {
                const seed = overrideEnabled && overrideText.trim()
                  ? overrideText
                  : composeGeneratedContextText(contextBlock);
                setEditDraft(seed);
                setEditingContext(true);
              }}
            >
              Edit Context
            </Button>
          )}
          <p className="text-[11px] text-muted-foreground italic">
            Generated from Assessment Profile, Telecom Service Models and Infrastructure Scope.
            Reviewer may edit the narrative; confirmation applies to whichever version is active.
          </p>
        </div>
      </CardContent>
    </Card>
  ) : null;

  const vendorExposureNode = isTelecomV2 ? (
    <TelecomVendorExposurePanel
      assessmentId={assessmentId}
      disabled={disabled}
      initialConfirmed={vendorExposureConfirmed}
      onConfirmationChange={setVendorExposureConfirmed}
    />
  ) : null;

  const narrativeNode = (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-foreground">Executive Summary</label>
          {isTelecomV2 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={insertSuggestedDraft}
              disabled={disabled || draftLoading}
              title="Regenerates the Executive Summary, Key Strengths, and Key Areas for Improvement using maturity bands only (no decimal scores, no NIST function names). Overwrites current text — confirms first if content exists."
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              {draftLoading ? "Generating…" : "Regenerate draft"}
            </Button>
          )}
        </div>
        <Textarea rows={6} value={form.executive_summary} onChange={e => update("executive_summary", e.target.value)} placeholder="Provide an overall executive summary of the cybersecurity assessment…" disabled={disabled} />
      </div>
      <div>
        <label className="text-sm font-medium text-foreground mb-1 block">Key Strengths</label>
        <Textarea rows={4} value={form.key_strengths} onChange={e => update("key_strengths", e.target.value)} placeholder="Highlight key areas where the organization demonstrates strong cybersecurity posture…" disabled={disabled} />
      </div>
      <div>
        <label className="text-sm font-medium text-foreground mb-1 block">Key Areas for Improvement</label>
        <Textarea rows={4} value={form.key_areas_for_improvement} onChange={e => update("key_areas_for_improvement", e.target.value)} placeholder="Identify critical gaps and areas requiring immediate attention…" disabled={disabled} />
      </div>
    </div>
  );

  const assessmentStatsNode = metrics ? (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <MetricCard label="Total Controls Assessed" value={metrics.totalControls} icon={<ShieldCheck className="h-4 w-4 text-primary" />} color="text-primary" />
      <MetricCard label="Implemented" value={metrics.implemented} color="text-green-600" />
      <MetricCard label="Partially Implemented" value={metrics.partial} color="text-amber-500" />
      <MetricCard label="Not Implemented" value={metrics.notImplemented} color="text-destructive" />
      <MetricCard label="Total Findings" value={metrics.totalFindings} color="text-foreground" />
      <MetricCard label="High Findings" value={metrics.highFindings} icon={<ShieldAlert className="h-4 w-4 text-destructive" />} color="text-destructive" />
      <MetricCard label="Moderate Findings" value={metrics.moderateFindings} icon={<AlertTriangle className="h-4 w-4 text-amber-500" />} color="text-amber-500" />
      <MetricCard label="Low Findings" value={metrics.lowFindings} icon={<Shield className="h-4 w-4 text-blue-500" />} color="text-blue-500" />
    </div>
  ) : (
    <p className="text-sm text-muted-foreground">No metrics available yet. Complete the questionnaire review first.</p>
  );

  const RISK_COLORS: Record<string, string> = {
    High: "hsl(var(--destructive))",
    Moderate: "hsl(38 92% 50%)", // amber-500
    Low: "hsl(217 91% 60%)",     // blue-500
  };
  const riskDistributionNode = riskDistTotal === 0 ? (
    <p className="text-sm text-muted-foreground">No findings recorded yet.</p>
  ) : (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
      <div className="relative h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={riskDistributionData.filter((r) => r.value > 0)}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              stroke="hsl(var(--background))"
              strokeWidth={2}
            >
              {riskDistributionData
                .filter((r) => r.value > 0)
                .map((entry) => (
                  <Cell key={entry.label} fill={RISK_COLORS[entry.label]} />
                ))}
            </Pie>
            <RTooltip
              contentStyle={{
                background: "hsl(var(--popover))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 6,
                fontSize: 12,
              }}
              formatter={(value: any, name: any) => [`${value} findings`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-3xl font-bold text-foreground">{riskDistTotal}</div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Findings</div>
        </div>
      </div>
      <div className="space-y-2">
        {riskDistributionData.map((r) => {
          const pct = riskDistTotal > 0 ? Math.round((r.value / riskDistTotal) * 100) : 0;
          return (
            <div key={r.label} className="flex items-center gap-3 text-sm">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ background: RISK_COLORS[r.label] }}
              />
              <span className="font-medium text-foreground flex-1">{r.label}</span>
              <span className="tabular-nums text-foreground">{r.value}</span>
              <span className="tabular-nums text-muted-foreground w-12 text-right">{pct}%</span>
            </div>
          );
        })}
        <p className="text-[11px] text-muted-foreground italic pt-2">
          Distribution of existing findings by reviewer-assigned risk level. No likelihood/impact model applied.
        </p>
      </div>
    </div>
  );

  // Legacy-style Findings Heatmap — three colored risk zones populated with one
  // dot per official finding. Driven exclusively by the Findings tab risk_level
  // (High / Moderate / Low). No likelihood/impact axis, no reviewer_score join.
  const RISK_ZONES: Array<{
    key: "High" | "Moderate" | "Low";
    label: string;
    bg: string;
    border: string;
    dot: string;
    text: string;
  }> = [
    { key: "High",     label: "High Risk",     bg: "rgba(220, 38, 38, 0.12)",  border: "#DC2626", dot: "#DC2626", text: "#7F1D1D" },
    { key: "Moderate", label: "Moderate Risk", bg: "rgba(245, 158, 11, 0.14)", border: "#F59E0B", dot: "#F59E0B", text: "#78350F" },
    { key: "Low",      label: "Low Risk",      bg: "rgba(22, 163, 74, 0.12)",  border: "#16A34A", dot: "#16A34A", text: "#14532D" },
  ];

  const riskHeatmapNode = findings.length === 0 ? (
    <p className="text-sm text-muted-foreground">No findings to chart.</p>
  ) : (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Findings Heatmap — one dot per official finding, placed in its risk zone using the
        risk level recorded on the Findings tab. No likelihood / impact methodology is applied.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {RISK_ZONES.map((z) => {
          const n = findingsByRisk[z.key] ?? 0;
          const dots = Array.from({ length: n });
          return (
            <div
              key={z.key}
              className="rounded-lg border p-3 flex flex-col"
              style={{ background: z.bg, borderColor: z.border, minHeight: 160 }}
            >
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold" style={{ color: z.text }}>{z.label}</span>
                <span className="text-2xl font-bold tabular-nums" style={{ color: z.border }}>{n}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 content-start flex-1">
                {dots.map((_, i) => (
                  <span
                    key={i}
                    className="inline-block rounded-full"
                    style={{ width: 10, height: 10, background: z.dot, boxShadow: `0 0 0 1px ${z.border} inset` }}
                    title={`${z.label} finding`}
                  />
                ))}
                {n === 0 && (
                  <span className="text-[11px] italic" style={{ color: z.text, opacity: 0.7 }}>
                    No findings
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground italic">
        Total dots = {findings.length} official finding{findings.length === 1 ? "" : "s"}.
      </p>
    </div>
  );


  const maturityRatingsNode = (
    <div className="space-y-6">
      {isTelecomV2 ? (
        <>
          <CyberResilienceFunctionalAdequacy
            assessmentId={assessmentId}
            refreshKey={nistRefreshKey}
            orgName={orgName}
            overrideBand={form.cyber_resilience_override ? (form.override_level as any) : null}
            findings={findings}
          />
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Reviewer Override</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.cyber_resilience_override}
                  onCheckedChange={(checked) => {
                    update("cyber_resilience_override", checked);
                    if (checked) update("override_level", calculatedLevel);
                  }}
                  disabled={disabled}
                />
                <label className="text-sm font-medium text-foreground">
                  Override calculated overall band
                </label>
              </div>
              {form.cyber_resilience_override ? (
                <div className="pl-12 mt-3">
                  <Select
                    value={form.override_level}
                    onValueChange={(val) => update("override_level", val)}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MATURITY_LEVELS.map(level => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Auto-calculated: <strong>{calculatedLevel}</strong>
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-2">
                  Automatically calculated from Assessment Results.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cyber Resilience &amp; Functional Adequacy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-center">
                <CyberResilienceDial
                  level={effectiveLevel}
                  averageScore={calculatedAvg}
                  controlsEvaluated={controlsEvaluated}
                  hideMetrics={false}
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={form.cyber_resilience_override}
                  onCheckedChange={(checked) => {
                    update("cyber_resilience_override", checked);
                    if (checked) update("override_level", calculatedLevel);
                  }}
                  disabled={disabled}
                />
                <label className="text-sm font-medium text-foreground">Override calculated score</label>
              </div>

              {form.cyber_resilience_override && (
                <div className="pl-12">
                  <Select
                    value={form.override_level}
                    onValueChange={(val) => update("override_level", val)}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MATURITY_LEVELS.map(level => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Auto-calculated: <strong>{calculatedLevel}</strong> (avg: {calculatedAvg.toFixed(2)})
                  </p>
                </div>
              )}

              {!form.cyber_resilience_override && (
                <p className="text-xs text-muted-foreground">
                  Automatically calculated from Assessment Results. Average score: {calculatedAvg.toFixed(2)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {isTelecomV2 ? (
        <CsvDomainRatingsAndCommentary
          assessmentId={assessmentId}
          refreshKey={csvDomainRefreshKey}
          onOverallScoreChange={form.cyber_resilience_override ? undefined : handleOverallScoreChange}
        />
      ) : (
        <CsvDomainScorecard
          assessmentId={assessmentId}
          refreshKey={csvDomainRefreshKey}
          onOverallScoreChange={form.cyber_resilience_override ? undefined : handleOverallScoreChange}
          hideNumeric={false}
        />
      )}

      {!isTelecomV2 && (
        <NistFunctionScorecard
          assessmentId={assessmentId}
          refreshKey={nistRefreshKey}
          hideNumeric={false}
        />
      )}
    </div>
  );

  const triggerCls = "px-4 py-3 hover:no-underline text-base font-medium";
  const itemCls = "border rounded-lg bg-card overflow-hidden";
  const contentCls = "px-4 pb-4 pt-2";

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex justify-end">
        <SaveChip state={autosave.state} />
      </div>

      <Accordion
        type="multiple"
        defaultValue={["executive-narrative", "operating-context"]}
        className="space-y-3"
      >
        <AccordionItem value="executive-narrative" className={itemCls}>
          <AccordionTrigger className={triggerCls}>Executive Narrative</AccordionTrigger>
          <AccordionContent className={contentCls}>{narrativeNode}</AccordionContent>
        </AccordionItem>

        {operatingContextNode && (
          <AccordionItem value="operating-context" className={itemCls}>
            <AccordionTrigger className={triggerCls}>Operating &amp; Regulatory Context</AccordionTrigger>
            <AccordionContent className={contentCls}>{operatingContextNode}</AccordionContent>
          </AccordionItem>
        )}

        {vendorExposureNode && (
          <AccordionItem value="vendor-exposure" className={itemCls}>
            <AccordionTrigger className={triggerCls}>Vendor Exposure</AccordionTrigger>
            <AccordionContent className={contentCls}>{vendorExposureNode}</AccordionContent>
          </AccordionItem>
        )}

        <AccordionItem value="assessment-statistics" className={itemCls}>
          <AccordionTrigger className={triggerCls}>Assessment Statistics</AccordionTrigger>
          <AccordionContent className={contentCls}>{assessmentStatsNode}</AccordionContent>
        </AccordionItem>

        <AccordionItem value="risk-distribution" className={itemCls}>
          <AccordionTrigger className={triggerCls}>Risk Distribution</AccordionTrigger>
          <AccordionContent className={contentCls}>{riskDistributionNode}</AccordionContent>
        </AccordionItem>

        <AccordionItem value="risk-heatmap" className={itemCls}>
          <AccordionTrigger className={triggerCls}>Findings Heatmap</AccordionTrigger>
          <AccordionContent className={contentCls}>{riskHeatmapNode}</AccordionContent>
        </AccordionItem>


        <AccordionItem value="maturity-ratings" className={itemCls}>
          <AccordionTrigger className={triggerCls}>Maturity &amp; Ratings</AccordionTrigger>
          <AccordionContent className={contentCls}>{maturityRatingsNode}</AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function MetricCard({ label, value, icon, color }: { label: string; value: number; icon?: React.ReactNode; color?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3 text-center">
      <div className="flex items-center justify-center gap-1.5 mb-1">
        {icon}
        <span className={`text-2xl font-bold ${color ?? "text-foreground"}`}>{value}</span>
      </div>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
